/**
 * v3.19-1430 · Company Drive — browses Google Drive using the same
 * OAuth connection that already powers Company Email + Google Chat.
 *
 * Features:
 *   • Browse My Drive root and drill into folders
 *   • Breadcrumb navigation
 *   • Server-side name search (Drive `q` grammar)
 *   • Open a file (uses `webViewLink` — opens in Google in a new tab,
 *     the closest supported "open" behaviour per Drive API)
 *   • Download a file (Hub proxies the bytes so downloads happen
 *     without ever leaving the Hub session)
 *   • Upload a file via multipart to Drive
 *
 * We deliberately do NOT re-render Google Docs inside Hub — the
 * Drive Preview API returns HTML that Google's own CSP blocks
 * from being iframed cross-origin.  `webViewLink` is the
 * Google-documented "open" contract.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  HardDrive, Folder, FileText, Loader2, ExternalLink, Search as SearchIcon,
  Upload, Download, ChevronRight, RefreshCw, Plus, X, AlertCircle, Home,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const humanSize = (n) => {
  const b = Number(n);
  if (!b || Number.isNaN(b)) return '';
  const kb = 1024;
  if (b < kb) return `${b} B`;
  if (b < kb * kb) return `${(b / kb).toFixed(1)} KB`;
  if (b < kb ** 3) return `${(b / kb / kb).toFixed(1)} MB`;
  return `${(b / kb ** 3).toFixed(1)} GB`;
};

const isFolder = (mime) => mime === 'application/vnd.google-apps.folder';

const MIME_ICON_LABEL = (mime) => {
  if (!mime) return 'File';
  if (mime.includes('folder')) return 'Folder';
  if (mime.includes('document')) return 'Doc';
  if (mime.includes('spreadsheet')) return 'Sheet';
  if (mime.includes('presentation')) return 'Slides';
  if (mime.startsWith('image/')) return 'Image';
  if (mime.includes('pdf')) return 'PDF';
  return 'File';
};

export default function CompanyDriveTab() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [breadcrumb, setBreadcrumb] = useState([{ id: null, name: 'Company Drive' }]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const currentFolder = breadcrumb[breadcrumb.length - 1];

  // -------- status
  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await api.get('/google/drive/status');
      setStatus(r.data);
    } catch (e) {
      if (e?.response?.status === 403) {
        setStatus({ connected: false, forbidden: true, forbidden_detail: e?.response?.data?.detail || 'Not authorized' });
      } else {
        setStatus({ connected: false });
      }
    } finally {
      setStatusLoading(false);
    }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const connect = async () => {
    try {
      const r = await api.get('/google/connect');
      const url = r.data?.authorization_url;
      if (url) window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to start Google connection');
    }
  };

  // -------- files
  const loadFiles = useCallback(async (opts = {}) => {
    const folderId = opts.folderId !== undefined ? opts.folderId : currentFolder.id;
    const searchQ  = opts.query !== undefined ? opts.query : (query || '');
    if (!status?.connected || !status?.has_drive_scopes) return;
    setLoading(true);
    try {
      const params = { page_size: 60 };
      if (searchQ) params.q = searchQ;
      else if (folderId) params.folder_id = folderId;
      const r = await api.get('/google/drive/files', { params });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Drive');
    } finally {
      setLoading(false);
    }
  }, [currentFolder.id, query, status]);
  useEffect(() => { if (status?.has_drive_scopes) loadFiles({ folderId: currentFolder.id, query: '' }); }, [status?.has_drive_scopes]);

  const openFolder = (item) => {
    setBreadcrumb((prev) => [...prev, { id: item.id, name: item.name }]);
    setQuery('');
    setTimeout(() => loadFiles({ folderId: item.id, query: '' }), 0);
  };
  const goBreadcrumb = (idx) => {
    const newB = breadcrumb.slice(0, idx + 1);
    setBreadcrumb(newB);
    setQuery('');
    setTimeout(() => loadFiles({ folderId: newB[newB.length - 1].id, query: '' }), 0);
  };

  const submitSearch = (e) => {
    e?.preventDefault?.();
    loadFiles({ query, folderId: query ? null : currentFolder.id });
  };

  const openFile = (item) => {
    if (item.webViewLink) {
      window.open(item.webViewLink, '_blank', 'noopener,noreferrer');
    } else {
      toast.info('This file has no viewable link.');
    }
  };

  const downloadFile = async (item) => {
    try {
      // We ask the backend to proxy the bytes so the download stays
      // scoped to the Hub session (no OAuth token exposed to browser).
      const r = await api.get(`/google/drive/files/${item.id}/download`, {
        responseType: 'blob',
      });
      const blob = r.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = item.name || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    }
  };

  const uploadPicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (currentFolder.id) fd.append('parent_id', currentFolder.id);
      const r = await api.post('/google/drive/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${r.data?.name || file.name}`);
      loadFiles({ folderId: currentFolder.id, query: '' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // -------- render
  if (statusLoading) {
    return (
      <Card data-testid="company-drive-tab">
        <CardContent className="py-6 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking Google connection…
        </CardContent>
      </Card>
    );
  }

  if (status?.forbidden) {
    return (
      <Card data-testid="company-drive-tab">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-slate-500" /> Company Drive
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900 flex items-start gap-2" data-testid="drive-forbidden">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{status.forbidden_detail || 'Company Drive is available to Prestigious Companies internal users only.'}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!status?.connected) {
    return (
      <Card data-testid="company-drive-tab">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-slate-500" /> Company Drive
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm text-slate-700">
            Connect your Google account to browse Company Drive from inside Hub.
          </div>
          <Button onClick={connect} data-testid="drive-connect-btn">
            <Plus className="h-4 w-4 mr-1" /> Connect Google Account
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!status?.has_drive_scopes) {
    return (
      <Card data-testid="company-drive-tab">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-slate-500" /> Company Drive
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              Your Google connection <span className="font-semibold">{status.email}</span> is missing Drive scopes.
              Reconnect to grant Drive read + upload access.
            </div>
          </div>
          <Button onClick={connect} data-testid="drive-reconnect-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Reconnect Google Account
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="company-drive-tab">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <HardDrive className="h-5 w-5 text-emerald-700" /> Company Drive
          <span className="text-[11px] text-slate-500 font-normal">as {status.email}</span>
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => loadFiles({})} title="Refresh" data-testid="drive-refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={uploadPicked}
            data-testid="drive-file-input"
          />
          <Button
            size="sm"
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="drive-upload-btn"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload</>}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search + breadcrumb */}
        <div className="flex items-center gap-3 mb-3">
          <form onSubmit={submitSearch} className="relative flex-1">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Drive by file name"
              className="pl-8 h-9"
              data-testid="drive-search-input"
            />
            {query && (
              <button
                type="button"
                onClick={() => { setQuery(''); loadFiles({ folderId: currentFolder.id, query: '' }); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </form>
          <Button size="sm" variant="outline" onClick={submitSearch} data-testid="drive-search-btn">Search</Button>
        </div>

        <div className="text-xs text-slate-500 mb-2 flex items-center flex-wrap gap-1">
          <Home className="h-3 w-3" />
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => goBreadcrumb(i)}
                className={`hover:underline ${i === breadcrumb.length - 1 ? 'text-slate-900 font-semibold' : 'text-emerald-700'}`}
                data-testid={`drive-breadcrumb-${i}`}
              >
                {b.name}
              </button>
              {i < breadcrumb.length - 1 && <ChevronRight className="h-3 w-3" />}
            </span>
          ))}
        </div>

        <div className="border border-slate-200 rounded-md overflow-hidden">
          {loading && (
            <div className="p-4 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {!loading && items.length === 0 && (
            <div className="p-8 text-sm text-slate-500 italic text-center">
              This folder is empty.  Upload a file with the button above.
            </div>
          )}
          {!loading && items.map((item) => {
            const folder = isFolder(item.mimeType);
            return (
              <div
                key={item.id}
                className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 group"
                data-testid={`drive-item-${item.id}`}
              >
                <button
                  type="button"
                  onClick={() => folder ? openFolder(item) : openFile(item)}
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                >
                  {folder ? (
                    <Folder className="h-5 w-5 text-amber-600 shrink-0" />
                  ) : (
                    <FileText className="h-5 w-5 text-slate-500 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-900 truncate">{item.name}</div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-2">
                      <span>{MIME_ICON_LABEL(item.mimeType)}</span>
                      {item.size && <><span>·</span><span>{humanSize(item.size)}</span></>}
                      {item.modifiedTime && <><span>·</span><span>Modified {new Date(item.modifiedTime).toLocaleDateString()}</span></>}
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!folder && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openFile(item)} title="Open in Drive" data-testid={`drive-open-${item.id}`}>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadFile(item)} title="Download" data-testid={`drive-dl-${item.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-2 text-[11px] text-slate-500" data-testid="drive-footer-shared">
          Rooted at the <span className="font-semibold">Prestigious Companies Shared Drive</span>{status?.shared_drive_root_id ? ` (${status.shared_drive_root_id})` : ''} · your personal My Drive is never shown here.  Hub proxies downloads so no tokens leave the server.
        </div>
      </CardContent>
    </Card>
  );
}
